/**
 * The demo's AI marketer reply (SKE-4166, phase 5) — client half.
 *
 * The visitor is playing their own client: they leave a note on a scheduled
 * post, and a moment later "their" marketer replies in the same thread. It is
 * the beat that turns the demo from a board you click into a conversation you
 * are having.
 *
 * ------------------------------ NEVER BROKEN --------------------------------
 * There is no error state and no empty state, for the same reason phase 4 has
 * none (see demoBrand.ts): a reply that sometimes says "something went wrong"
 * is worse than a reply that is sometimes generic. Every failure — endpoint
 * unset, offline, rate-limited, disabled server-side, no model key, malformed
 * body — resolves to a canned in-character line from `demoStrings`, so the
 * thread always answers. `source` records which happened so the model's real
 * hit-rate can be measured honestly rather than flattered.
 *
 * ------------------- THE REPLY IS STILL A CLIENT-ONLY WRITE -----------------
 * This module only *fetches text*. The reply is written into the visitor's own
 * localStorage snapshot by `useDemoAiReply`, exactly like every other demo
 * mutation — see the hard safety invariant at the top of demoApi.ts. It is
 * never posted through the real collaboration comment API, and this endpoint
 * cannot post it either: it writes nothing at all.
 *
 * NO CREDENTIALS: the request is anonymous and `credentials` is left at the
 * default `same-origin`, so no cookie is ever sent cross-origin.
 */

import { demoText, type DemoStringKey } from './demoStrings'

/** Path on schedugram-server. Kept in one place; the server mirrors it. */
export const DEMO_AI_REPLY_PATH = '/public/demo-ai-reply'

/**
 * Reuses `VITE_DEMO_LEAD_API_URL` for the same reason phase 4 does: all three
 * public demo endpoints live on the same schedugram-server, and a third
 * variable naming the same origin is one more thing to get wrong in four `.env`
 * files. If they ever diverge, split it then.
 *
 * An unset base URL is survivable — `null` means we skip the call and use a
 * canned line, which is what a disabled server would have produced anyway. So,
 * unlike the phase-3 lead gate, nothing is hidden when this is unconfigured.
 */
export const getDemoAiReplyEndpoint = (): string | null => {
  const base = import.meta.env.VITE_DEMO_LEAD_API_URL?.trim()

  if (!base) {
    return null
  }

  return `${base.replace(/\/+$/, '')}${DEMO_AI_REPLY_PATH}`
}

/** Mirrors the server's clamps; the server re-applies all of them. */
const MAX_COMMENT = 600
const MAX_CAPTION = 400
const MAX_LABEL = 60

const clamp = (value: string | undefined, max: number): string =>
  (value ?? '').replace(/\s+/g, ' ').trim().slice(0, max)

export interface DemoAiReplyPayload {
  comment: string
  caption?: string
  brandName?: string
  vertical?: string
  sessionId?: string
  commentId?: string
}

export interface BuildDemoAiReplyPayloadInput {
  comment: string
  caption?: string
  brandName?: string
  vertical?: string
  sessionId?: string
  commentId?: string
}

/**
 * Pure payload builder — the shape the server validates against, assembled in
 * one place so it can be tested without a DOM or a network.
 *
 * Optional context is omitted rather than sent empty: the server treats an
 * absent caption as "a scheduled social post", and sending `""` would make the
 * two cases indistinguishable in a request log.
 */
export const buildDemoAiReplyPayload = (
  input: BuildDemoAiReplyPayloadInput
): DemoAiReplyPayload => {
  const caption = clamp(input.caption, MAX_CAPTION)
  const brandName = clamp(input.brandName, MAX_LABEL)
  const vertical = clamp(input.vertical, MAX_LABEL)

  return {
    comment: clamp(input.comment, MAX_COMMENT),
    ...(caption ? { caption } : {}),
    ...(brandName ? { brandName } : {}),
    ...(vertical ? { vertical } : {}),
    ...(input.sessionId ? { sessionId: input.sessionId } : {}),
    ...(input.commentId ? { commentId: input.commentId } : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* The local fallback                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The lines used when the endpoint never answers.
 *
 * They intentionally mirror the server's own canned set (see
 * `services/demoAiReplyPrompt.ts`) so an offline visitor and a visitor whose
 * server has no model key see the same demo. They live in `demoStrings` rather
 * than inline so the whole feature stays translatable in one file.
 */
const LOCAL_FALLBACK_KEYS: DemoStringKey[] = [
  'aiReplyFallbackOne',
  'aiReplyFallbackTwo',
  'aiReplyFallbackThree',
  'aiReplyFallbackFour',
  'aiReplyFallbackFive',
]

/**
 * Pick a canned reply, seeded from the comment id rather than random so the
 * same note always produces the same reply: a visitor who refreshes must not
 * watch their marketer say something different about the same message.
 *
 * FNV-1a, matching `deriveBrandAccent` in demoBrand.ts and the server's
 * `pickCannedDemoReply` — the same seed lands on the same index in both.
 */
export const pickLocalDemoReply = (seed = ''): string => {
  let hash = 0x811c9dc5

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }

  return demoText(LOCAL_FALLBACK_KEYS[hash % LOCAL_FALLBACK_KEYS.length])
}

/* -------------------------------------------------------------------------- */
/* The request                                                                 */
/* -------------------------------------------------------------------------- */

/** Where the line the visitor is reading actually came from. */
export type DemoAiReplySource =
  /** A real completion. */
  | 'model'
  /** The server answered, but with its own canned line (gated, capped, failed). */
  | 'fallback'
  /** The server was never reached — unconfigured, offline, or rate-limited. */
  | 'offline'

export interface DemoAiReplyResult {
  /** Always a friendly, in-character line. Never empty. */
  reply: string
  source: DemoAiReplySource
}

/**
 * Ask the server for the marketer's reply, falling back locally on anything at
 * all.
 *
 * Total: this never rejects and never returns an empty string, so the caller
 * has no failure branch to write. That is the contract the typing indicator
 * depends on — something always arrives to replace it.
 */
export const fetchDemoAiReply = async (
  payload: DemoAiReplyPayload
): Promise<DemoAiReplyResult> => {
  const local: DemoAiReplyResult = {
    reply: pickLocalDemoReply(payload.commentId ?? payload.comment),
    source: 'offline',
  }

  const endpoint = getDemoAiReplyEndpoint()
  if (!endpoint) {
    return local
  }

  let response: Response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
  } catch {
    return local
  }

  // 429 (per-IP limit), 400 (a note the server would not take) and anything
  // else all mean the same thing here: use the local line and keep going.
  if (!response.ok) {
    return local
  }

  try {
    const body = (await response.json()) as {
      reply?: unknown
      source?: unknown
    }

    const reply = typeof body?.reply === 'string' ? body.reply.trim() : ''
    if (!reply) {
      return local
    }

    return {
      reply,
      // Only claim `model` when the server did — otherwise the analytics would
      // over-report real completions.
      source: body?.source === 'model' ? 'model' : 'fallback',
    }
  } catch {
    return local
  }
}
